'use client'

import { useCallback, useEffect, useMemo } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { UseFormReturn } from 'react-hook-form'
import { DynamicForm } from '@skop-app/lib-front/dynamic-form'
import type { Customer, LabelType, OrderItem } from '@skop-app/lib-domain/types'

import { getQuoteFields } from '@/schemas/quote.schema'
import type { OrderModalValues, SignDocumentTabOptions } from '@/types'
import { useCustomersByOrganization } from '@/services/queries'
import { useUserContext } from '@/context'
import {
  sortOrderItemsWithServicesLast,
  useProductsGrouped,
} from '@/features/quote/hooks/useProductsGrouped'
import { parseLocaleNumber } from '@/schemas/schemaValidations'
import { toNumber } from '@/utils/helpers'

const parseLineAmount = (value: unknown, locale: string): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = parseLocaleNumber(value.trim(), locale)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return toNumber(value)
}

const formatRawTotalDisplay = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)

type WatchType = {
  name: string
  values: {
    currency: LabelType & {
      sign: string
    }
    vatPercentage: LabelType
    document: LabelType
    customer:
      | (LabelType & {
          email?: string
          phone?: string
          address?: string
          city?: string
          zipcode?: string
          country?: string
        })
      | null
    items: (OrderItem & { remainingQuantity: number })[]
  }
}

type ItemWithNature = {
  nature?: string
  rawProduct?: {
    nature?: string
  } | null
}

const isServiceItem = (item: ItemWithNature | null | undefined): boolean => {
  if (!item) return false
  const itemWithNature = item as ItemWithNature
  return (
    itemWithNature.nature === 'service' ||
    itemWithNature.rawProduct?.nature === 'service'
  )
}

/** Only changes when line items are added/removed/reidentified or quote metadata structure changes — not on quantity/price edits. */
const getOrderStructureKey = (
  orderValues: OrderModalValues | undefined,
  locale: string,
): string => {
  if (!orderValues) return `empty:${locale}`
  const items = orderValues.items ?? []
  return JSON.stringify({
    locale,
    len: items.length,
    rowIds: items.map((i) => [i.id ?? '', i.productID ?? ''].join(':')),
    currency: orderValues.currency?.value,
    vat: orderValues.vatPercentage?.value,
    document: orderValues.document?.value,
    customer: orderValues.customer?.value ?? orderValues.customerID,
    requestType: orderValues.requestType,
  })
}

const buildFormattedOrderValuesForForm = (
  orderValues: OrderModalValues | undefined,
  locale: string,
  productIdToMaxQuantity: Map<string, number>,
): OrderModalValues => {
  if (!orderValues?.items) return orderValues as OrderModalValues

  return {
    ...orderValues,
    items: (sortOrderItemsWithServicesLast(orderValues.items) ?? []).map(
      (item) => {
        const rawTotalValue = item.rawTotal
        let formattedRawTotal: string | number | undefined = rawTotalValue

        if (typeof rawTotalValue === 'number') {
          formattedRawTotal = formatRawTotalDisplay(rawTotalValue, locale)
        } else if (typeof rawTotalValue === 'string') {
          const rawTotalStr = rawTotalValue as string
          const numValue = parseLocaleNumber(rawTotalStr.trim(), locale)
          formattedRawTotal = Number.isFinite(numValue)
            ? formatRawTotalDisplay(numValue, locale)
            : rawTotalStr
        }

        const maxQuantityFromList =
          !isServiceItem(item) && item.productID
            ? productIdToMaxQuantity.get(item.productID)
            : undefined
        const maxQuantity =
          maxQuantityFromList != null
            ? `/ ${maxQuantityFromList}`
            : ((item as { maxQuantity?: string }).maxQuantity ?? '—')

        return {
          ...item,
          rawTotal: formattedRawTotal,
          maxQuantity,
        }
      },
    ),
  } as OrderModalValues
}

type ProductsFormProps = {
  form: React.RefObject<UseFormReturn | null>
  orderValues: OrderModalValues
  onSubmit(values: OrderModalValues): void
  setSignDocument: React.Dispatch<React.SetStateAction<LabelType | null>>
  setOrderValues: React.Dispatch<React.SetStateAction<OrderModalValues>>
  className?: string
  isLoading?: boolean
  options?: SignDocumentTabOptions
  nameAndEmail?: boolean
}

export const ProductsForm = ({
  form,
  orderValues,
  onSubmit,
  setSignDocument,
  setOrderValues,
  className,
  isLoading,
  options,
}: ProductsFormProps) => {
  const t = useTranslations()
  const locale = useLocale()
  const { team } = useUserContext()
  const { data: customersData } = useCustomersByOrganization(team?.id)

  const { productIdToMaxQuantity } = useProductsGrouped()

  const customerOptions = customersData
    ?.map((customer: Customer) => {
      const label = [customer.lastName, customer.firstName]
        .filter(Boolean)
        .join(' ')
        .trim()

      return {
        ...customer,
        label: label || customer.email || '',
        value: customer.id,
      }
    })
    .filter((customer) => Boolean(customer.label))

  const inventorySnapshot = useMemo(
    () => JSON.stringify([...productIdToMaxQuantity.entries()].sort()),
    [productIdToMaxQuantity],
  )

  const structureKeyFromOrder = getOrderStructureKey(orderValues, locale)

  const orderStructureKey = useMemo(
    () => `${structureKeyFromOrder}:${inventorySnapshot}`,
    [structureKeyFromOrder, inventorySnapshot],
  )

  const stableFormValues = useMemo(() => {
    return buildFormattedOrderValuesForForm(
      orderValues,
      locale,
      productIdToMaxQuantity,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only rebuild when orderStructureKey changes; orderValues/locale/productIdToMaxQuantity are read at that time
  }, [orderStructureKey])

  useEffect(() => {
    const subscription = form.current?.watch(
      (_, { name, values }: WatchType) => {
        if (!name || !values) return

        if (name === 'vatPercentage') {
          setOrderValues((prev: OrderModalValues) => {
            return {
              ...prev,
              vatPercentage: values.vatPercentage,
            } as OrderModalValues
          })
        }

        if (name === 'currency') {
          setOrderValues((prev: OrderModalValues) => {
            return {
              ...prev,
              currency: values.currency,
            }
          })
        }

        if (name === 'document') {
          values?.document?.value
            ? setSignDocument({
                label: values.document.label,
                value: values.document.value,
              })
            : setSignDocument(null)
          return
        }

        if (name === 'customer') {
          const customer = values.customer
          if (!customer?.value) {
            setOrderValues((prev: OrderModalValues) => ({
              ...prev,
              name: '',
              email: '',
              phone: undefined,
              customerID: undefined,
              customer: null,
              billingAddress: undefined,
              deliveryAddress: undefined,
            }))
            return
          }

          setOrderValues((prev: OrderModalValues) => {
            const label = customer.label || prev.name || ''
            const email = customer.email || prev.email || ''

            return {
              ...prev,
              name: label,
              email,
              phone: customer.phone,
              customerID: customer.value,
              customer: {
                label,
                value: customer.value,
                email,
                phone: customer.phone,
              },
              billingAddress: {
                name: label,
                email,
                phone: customer.phone,
                address: customer.address,
                city: customer.city,
                zipcode: customer.zipcode,
                country: customer.country,
              },
              deliveryAddress: {
                name: label,
                email,
                phone: customer.phone,
                address: customer.address,
                city: customer.city,
                zipcode: customer.zipcode,
                country: customer.country,
              },
            } as OrderModalValues
          })
        }

        if (name.startsWith('items')) {
          const formApi = form.current
          setOrderValues((prev: OrderModalValues) => {
            const previousItems = prev.items ?? []
            const incomingItems = values?.items ?? previousItems
            const hasLengthChange =
              incomingItems.length !== previousItems.length

            if (hasLengthChange) {
              if (name === 'items') {
                return {
                  ...prev,
                  items: incomingItems,
                } as OrderModalValues
              }
              return prev
            }

            const mergedItems = previousItems.map((prevItem, index) => {
              const formItem = incomingItems[index]
              if (!formItem) return prevItem

              const quantity = toNumber(formItem.quantity)
              const rawPrice = toNumber(formItem.rawPrice)

              let cappedQuantity = quantity
              if (!isServiceItem(prevItem) && prevItem.productID) {
                const maxQuantity = productIdToMaxQuantity.get(
                  prevItem.productID,
                )
                if (maxQuantity != null) {
                  cappedQuantity = Math.min(quantity, maxQuantity)
                }
              }

              const rawTotal = Number((rawPrice * cappedQuantity).toFixed(2))

              if (
                toNumber(formItem.quantity) === cappedQuantity &&
                toNumber(formItem.rawPrice) === rawPrice &&
                Math.abs(
                  parseLineAmount(formItem.rawTotal, locale) - rawTotal,
                ) < 0.000_01
              ) {
                return prevItem
              }

              return {
                ...prevItem,
                quantity: cappedQuantity,
                rawPrice,
                rawTotal,
              }
            })

            const hasChange = mergedItems.some(
              (item, index) => item !== previousItems[index],
            )
            if (!hasChange) return prev

            if (formApi) {
              mergedItems.forEach((item, index) => {
                const path = `items[${index}]`
                const fv = formApi.getValues(path)
                if (!fv) return
                if (toNumber(fv.quantity) !== item.quantity) {
                  formApi.setValue(`${path}.quantity`, item.quantity, {
                    shouldDirty: true,
                    shouldValidate: false,
                  })
                }
                if (toNumber(fv.rawPrice) !== toNumber(item.rawPrice)) {
                  formApi.setValue(`${path}.rawPrice`, item.rawPrice, {
                    shouldDirty: true,
                    shouldValidate: false,
                  })
                }
                const numericTotal = toNumber(item.rawTotal)
                const targetDisplay = formatRawTotalDisplay(
                  numericTotal,
                  locale,
                )
                const parsedDisplay = parseLineAmount(fv.rawTotal, locale)
                const needsValueSync =
                  Math.abs(parsedDisplay - numericTotal) > 0.000_01
                const needsFormatSync =
                  !needsValueSync &&
                  (typeof fv.rawTotal === 'number' ||
                    (typeof fv.rawTotal === 'string' &&
                      fv.rawTotal.trim() !== targetDisplay))
                if (needsValueSync || needsFormatSync) {
                  formApi.setValue(`${path}.rawTotal`, targetDisplay, {
                    shouldDirty: true,
                    shouldValidate: false,
                  })
                }
              })
            }

            return {
              ...prev,
              items: mergedItems,
            } as OrderModalValues
          })
        }
      },
    )
    return () => {
      subscription?.unsubscribe()
    }
  }, [form, locale, productIdToMaxQuantity, setOrderValues, setSignDocument])

  const quoteFields = getQuoteFields({
    t,
    locale,
    currency: orderValues?.currency?.sign || '€',
    isLoading,
    options,
    addProducts: !options?.hideAddProducts,
    orderValues,
    setOrderValues,
    customerOptions,
  })

  const handleSubmit = useCallback(
    (formData: OrderModalValues) => {
      const normalizedData = {
        ...formData,
        items: sortOrderItemsWithServicesLast(formData.items)?.map((item) => {
          const rawTotalValue = item.rawTotal
          let normalizedRawTotal: number | undefined

          if (typeof rawTotalValue === 'number') {
            normalizedRawTotal = rawTotalValue
          } else if (rawTotalValue != null && rawTotalValue !== '') {
            const parsed = parseLocaleNumber(
              String(rawTotalValue).trim(),
              locale,
            )
            normalizedRawTotal = Number.isFinite(parsed) ? parsed : undefined
          } else {
            normalizedRawTotal = undefined
          }

          return {
            ...item,
            rawTotal: normalizedRawTotal,
          }
        }),
      }
      onSubmit(normalizedData)
    },
    [locale, onSubmit],
  )

  return (
    <DynamicForm
      ref={form}
      fields={quoteFields}
      values={stableFormValues}
      onSubmit={handleSubmit}
      className={className}
      locale={locale}
    />
  )
}
